/**
 * Side-rail pin preference — the one bit of layout state that outlives a page
 * load.
 *
 * The rail has three visual states (see `SideRail.tsx`); only one of them is a
 * *choice*: pinned open. Hover- and focus-driven expansion is transient and
 * deliberately not persisted — it would make the rail's width depend on where
 * the mouse happened to be when the tab was closed.
 *
 * Default is collapsed: no stored value means the icon rail, for everyone.
 *
 * Storage sits alongside the session token and the active-project pick (same
 * `coffman-tm:` prefix) — see `activeProject.ts`, whose Storage guard this
 * copies: the web Vitest project runs in the `node` environment, where
 * `localStorage` does not exist.
 */

export const RAIL_STORAGE_KEY = 'coffman-tm:rail';

/**
 * Pre-2026-07-27 key, written by the old two-state toggle ('1' = collapsed).
 * Read once so the two PMs who explicitly chose the wide rail keep it, then
 * dropped. Delete this branch once no live browser can still hold the key.
 */
const LEGACY_RAIL_KEY = 'rail-collapsed';

function localStore(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Safari private mode / blocked storage throws on access.
    return null;
  }
}

/**
 * Is the rail pinned open? False — collapsed — on a first visit.
 *
 * Migrates a legacy preference in place on first read, which is why this is not
 * a pure getter.
 */
export function getRailPinned(): boolean {
  const store = localStore();
  if (!store) return false;

  const raw = store.getItem(RAIL_STORAGE_KEY);
  if (raw === 'pinned') return true;
  if (raw === 'collapsed') return false;

  const legacy = store.getItem(LEGACY_RAIL_KEY);
  if (legacy === null) return false;
  const pinned = legacy === '0';
  try {
    store.setItem(RAIL_STORAGE_KEY, pinned ? 'pinned' : 'collapsed');
    store.removeItem(LEGACY_RAIL_KEY);
  } catch {
    // Quota / blocked storage: the migration is a nicety, not a requirement.
  }
  return pinned;
}

export function setRailPinned(pinned: boolean): void {
  try {
    localStore()?.setItem(RAIL_STORAGE_KEY, pinned ? 'pinned' : 'collapsed');
  } catch {
    // Ditto — a preference that fails to persist is not worth a crash.
  }
}
